import { useEffect } from 'react';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Lightbulb, Heart } from 'lucide-react';

const values = [
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'We embrace cutting-edge technology to deliver exceptional results for our clients.'
  },
  {
    icon: Heart,
    title: 'Passion',
    description: 'We love what we do and it shows in every project we deliver.'
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We work closely with our clients to understand their unique needs and goals.'
  },
  {
    icon: Target,
    title: 'Results',
    description: 'We\'re focused on delivering measurable outcomes that help businesses grow.'
  }
];

const team = [
  {
    name: 'Alex Chen',
    role: 'Founder & CEO',
    bio: 'Former Google engineer with a passion for democratizing web development.'
  },
  {
    name: 'Sarah Williams',
    role: 'Head of Design',
    bio: 'Award-winning designer with 10+ years of experience in digital product design.'
  },
  {
    name: 'Marcus Johnson',
    role: 'Lead Developer',
    bio: 'Full-stack developer specializing in React, Node.js, and cloud architecture.'
  },
  {
    name: 'Emily Park',
    role: 'AI Specialist',
    bio: 'Machine learning expert focused on creating intelligent design systems.'
  }
];

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About | TryWeb';
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative py-24 hero-glow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                <span className="text-gradient">About TryWeb</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-6">
                We're on a mission to make professional web development accessible to every business, 
                regardless of size or budget.
              </p>
              <p className="text-muted-foreground">
                Founded in 2023, TryWeb combines the power of artificial intelligence with expert 
                craftsmanship to create stunning websites and applications that help businesses thrive 
                in the digital age.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img 
                  src="https://images.unsplash.com/photo-1758518729685-f88df7890776?w=800&q=80"
                  alt="TryWeb Team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center">Our Story</h2>
          <div className="prose prose-invert max-w-none space-y-6 text-muted-foreground">
            <p>
              TryWeb was born from a simple observation: too many great businesses were being held back 
              by the lack of a professional online presence. Traditional web development was expensive, 
              time-consuming, and often out of reach for small businesses.
            </p>
            <p>
              We believed there had to be a better way. By leveraging the latest advances in artificial 
              intelligence, we've created a system that can generate custom, professional websites in a 
              fraction of the time and cost of traditional development.
            </p>
            <p>
              But we're not just about speed and cost savings. We're passionate about quality. Every 
              website we create is reviewed by our team of human experts to ensure it meets our high 
              standards. The result is a website that looks like it cost thousands, delivered at a 
              price that any business can afford.
            </p>
            <p>
              Today, we've helped hundreds of businesses across the UK establish their online presence. 
              From local cafes to legal firms, from auto shops to landscaping companies - we've seen 
              firsthand how the right website can transform a business.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Our Values</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These core values guide everything we do at TryWeb.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Want to Work With Us?</h2>
          <p className="text-muted-foreground mb-8">
            Let's create something amazing together. Get in touch to discuss your project.
          </p>
          <Link to="/contact">
            <Button size="lg" className="glow-primary gap-2" data-testid="about-cta">
              Contact Us <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </PageLayout>
  );
};

export default AboutPage;
